import React, { useState } from 'react'
import { CheckCircleIcon, LinkIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'

interface RedditOAuthButtonProps {
  isAuthorized: boolean
  onConnect: () => Promise<string> // Returns auth URL
  onDisconnect: () => Promise<void>
  disabled?: boolean
}

const RedditOAuthButton: React.FC<RedditOAuthButtonProps> = ({
  isAuthorized,
  onConnect,
  onDisconnect,
  disabled = false,
}) => {
  const queryClient = useQueryClient()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      // Get auth URL from backend
      const authUrl = await onConnect()

      // Open OAuth popup
      const width = 600
      const height = 700
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        authUrl,
        'reddit_oauth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      )

      if (!popup) {
        toast.error('Please allow popups for this site to connect your Reddit account')
        setIsConnecting(false)
        return
      }

      // Listen for postMessage from OAuth callback
      const handleMessage = async (event: MessageEvent) => {
        // Security: verify origin if needed
        if (event.data?.type === 'reddit_oauth_success') {
          clearInterval(checkInterval)
          window.removeEventListener('message', handleMessage)

          // Invalidate settings query to refetch fresh data
          await queryClient.invalidateQueries({ queryKey: ['settings'] })

          setIsConnecting(false)
          toast.success('Reddit account connected successfully!', { duration: 3000 })
        }
      }

      window.addEventListener('message', handleMessage)

      // Poll for popup close
      const checkInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkInterval)
          window.removeEventListener('message', handleMessage)
          setIsConnecting(false)
        }
      }, 500)

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkInterval)
        window.removeEventListener('message', handleMessage)
        if (!popup.closed) {
          popup.close()
          setIsConnecting(false)
        }
      }, 5 * 60 * 1000)
    } catch (error: any) {
      toast.error(error.message || 'Failed to start OAuth flow')
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Reddit account? This will stop all monitoring.')) {
      return
    }

    setIsDisconnecting(true)
    try {
      await onDisconnect()
      toast.success('Reddit account disconnected successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to disconnect Reddit account')
    } finally {
      setIsDisconnecting(false)
    }
  }

  if (isAuthorized) {
    return (
      <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center">
          <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
          <div>
            <p className="text-sm font-medium text-green-900">Reddit Account Connected</p>
            <p className="text-xs text-green-700">Your Reddit app is authorized and ready to use</p>
          </div>
        </div>
        <button
          onClick={handleDisconnect}
          disabled={disabled || isDisconnecting}
          className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
        >
          {isDisconnecting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Disconnecting...
            </>
          ) : (
            <>
              <XCircleIcon className="h-4 w-4 mr-2" />
              Disconnect
            </>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-center">
        <LinkIcon className="h-6 w-6 text-yellow-500 mr-3" />
        <div>
          <p className="text-sm font-medium text-yellow-900">Connect Your Reddit Account</p>
          <p className="text-xs text-yellow-700">
            Connect your Reddit account to start monitoring and posting
          </p>
        </div>
      </div>
      <button
        onClick={handleConnect}
        disabled={disabled || isConnecting}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
      >
        {isConnecting ? (
          <>
            <LoadingSpinner size="sm" className="mr-2" />
            Connecting...
          </>
        ) : (
          <>
            <LinkIcon className="h-4 w-4 mr-2" />
            Connect Reddit Account
          </>
        )}
      </button>
    </div>
  )
}

export default RedditOAuthButton
